// @ts-strict-ignore
import { BaseDynamicArticleConnectProcessor } from './base-dynamic-article-connect.processor';
import { CreateLabelingHelper } from '../../../../label/helper/create-labeling.helper';
import { OverlayService } from '@shared/overlay/overlay.service';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ConnectArticlePrepareData } from '../insert/connect-article-prepare-data';
import { UserUtils } from '@user/service/user-utils';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { AlertButton } from '@shared/alert/alertButton';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { Connection } from '@3d-scene/model3d/connection';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { LabelingArticleRotationHelper } from '../../../../label/helper/labeling-article-rotation.helper';
import { AcceptingConnectionSortGroupHelper } from '@3d-scene/helper/connection/accepting-connection-sort-group.helper';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleChangedService } from '@3d-scene/service/article-changed.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ProjectHelper } from '@project/helper/project.helper';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { IsProjectType } from '@project/model/is-project-type';
import { ArticleUsageService } from '@article/service/article-usage.service';

export class BaseDynamicLabelingArticleConnectProcessor extends BaseDynamicArticleConnectProcessor {
  // Maximal recommended lenght for marking strip; doubled compared to Flex version
  public static readonly MAX_LABELING_WIDTH: number = 11000;

  protected _validateSizes: boolean = true;

  constructor(
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    localeResourceManager: LocaleResourceManager,
    articleContainerLoader: ArticleContainerLoader,
    compositeArticleCacheManager: CompositeArticleCacheManager,
    articleContainerFactory: ArticleContainerFactory,
    articleManager: ArticleManager,
    compositeArticleUtils: CompositeArticleUtils,
    compositeArticleInfoUtils: CompositeArticleInfoUtils,
    articleContainerUtils: ArticleContainerUtils,
    acceptingConnectionSortGroupHelper: AcceptingConnectionSortGroupHelper,
    containerService: ContainerService,
    articleChangedService: ArticleChangedService,
    detachArticleUsageService: DetachArticleUsageService,
    private projectHelper: ProjectHelper,
    private alertHelper: AlertHelper,
    private createLabelingHelper: CreateLabelingHelper,
    private overlayService: OverlayService,
    private labelingArticleRotationHelper: LabelingArticleRotationHelper,
    private labelingUtils: LabelingUtils,
    articleContainerService: ArticleContainerService,
    private articleUsageService: ArticleUsageService,
  ) {
    super(
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      articleContainerLoader,
      compositeArticleCacheManager,
      articleContainerFactory,
      articleManager,
      compositeArticleUtils,
      compositeArticleInfoUtils,
      articleContainerUtils,
      localeResourceManager,
      acceptingConnectionSortGroupHelper,
      containerService,
      articleChangedService,
      detachArticleUsageService,
      articleContainerService,
    );
  }

  protected internalPreProcess(article: ArticleVO, connectPrepareData: ConnectArticlePrepareData, handler: Function): void {
    const articleWrappers: ArticleContainersWrapper[] = this.prepareDynamicLabelingArticleWrappers(article, connectPrepareData);
    if (this.validateWidthOfArticles(articleWrappers, handler)) {
      handler(articleWrappers);
    }
  }

  protected prepareDynamicLabelingArticleWrappers(article: ArticleVO, connectPrepareData: ConnectArticlePrepareData): ArticleContainersWrapper[] {
    const hasPermissionToEditAllLabelings: boolean = UserUtils.hasPermissionToEditAllLabelings(this.projectModel.user);
    if (!(hasPermissionToEditAllLabelings || ConnectionInfoUtils.isDefaulLabelingConnectionCubes(connectPrepareData.acceptingConnections))) {
      throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.error_connect_labeling'));
    }
    return super.preProcessWithoutRefresh(
      article,
      connectPrepareData.acceptingConnections,
      connectPrepareData.vectorId,
      connectPrepareData.givingConnectionNames
    );
  }

  private validateWidthOfArticles(articleWrappers: ArticleContainersWrapper[], handler: Function): boolean {
    const invalidWrappers = articleWrappers.filter(articleWrapper => {
      const acceptingRectangle = articleWrapper.properties.getValue(ArticleContainerPropertyDef.RECTANGLE);
      return acceptingRectangle && acceptingRectangle.width > BaseDynamicLabelingArticleConnectProcessor.MAX_LABELING_WIDTH;
    });

    if (invalidWrappers.length > 0) {
      // Case for CAE import
      let showOverlay: boolean = false;
      if (!this.overlayService.isOverlayRemovable) {
        showOverlay = true;
      }
      this.overlayService.hideAllOverlays();

      this.alertHelper.showAlert(
        "connectProcessor.title.error_connect_too_long_labeling",
        "'connectProcessor.message.error_connect_too_long_labeling",
        undefined,
        [AlertButton.YES, AlertButton.NO],
        "help")
        .then((response: AlertButton) => {
          if (AlertButton.NO === response) {
            articleWrappers = [];
          } else {
            this._validateSizes = false;
          }
          if (showOverlay) {
            this.overlayService.requestOverlayShow(OverlayBackground.color);
            this.overlayService.isOverlayRemovable = false;
          }
          handler(articleWrappers);
        });
      return false;
    }
    return true;
  }

  protected calculateAcceptingRectangleByStartEndConnection(
    closestConnectionCube: Connection,
    farestConnectionCube: Connection,
    connections: Connection[]
  ): Rectangle3D {
    let closestRectangle: Rectangle3D = null;
    let farestRectangle: Rectangle3D = null;

    if (this.labelingUtils.isConnectionsOfDifferentAdapters([closestConnectionCube, farestConnectionCube])) {
      closestRectangle = this.labelingUtils.calculateAdapterLabelingRectangle(closestConnectionCube);
      farestRectangle = this.labelingUtils.calculateAdapterLabelingRectangle(farestConnectionCube);
    } else {
      closestRectangle = this.calculateConnectionRectangle(closestConnectionCube);
      farestRectangle = this.calculateConnectionRectangle(farestConnectionCube);
    }

    return this.calculateAcceptingRectangleByStartEndRectangle(closestRectangle, farestRectangle, connections);
  }

  protected calculateConnectionRectangle(connectionCube: Connection): Rectangle3D {
    return this.labelingUtils.calculateLabelingRectangle(connectionCube);
  }

  protected createAcceptingConnectionGroups(sortedConnections: Connection[]): Connection[][] {
    return IsProjectType.mounting(this.projectModel.currentProject)
      ? this.prepareAndSplitConnectionCubes(sortedConnections, this.projectModel.ignoreSplitConnectionCubes)
      : super.createAcceptingConnectionGroups(sortedConnections);
  }

  protected createArticleWrapperByConnectionGroup(
    article: ArticleVO,
    connectionGroup: Connection[],
    connectionRectangle: Rectangle3D,
    searchVectorId: number,
    givingConnectionNames: string[]
  ): ArticleContainersWrapper {
    const articleWrapper: ArticleContainersWrapper = super.createArticleWrapperByConnectionGroup(
      article,
      connectionGroup,
      connectionRectangle,
      searchVectorId,
      givingConnectionNames
    );

    if (this.checkAndSetForDifferentAdaptersWithSameParent(articleWrapper, connectionGroup)) {
      return articleWrapper;
    }

    if (LabelingUtils.extractAdapterConnectionCubes(connectionGroup).length > 1) {
      const articlesBetweenContainers: ArticleContainer[] = this.createLabelingHelper.collectArticlesBetweenContainers(
        connectionGroup[0].articleContainer,
        connectionGroup[connectionGroup.length - 1].articleContainer,
        true
      );
      this.setArticleGuidsBetweenAdapters(articleWrapper, articlesBetweenContainers);
    }
    return articleWrapper;
  }

  private setArticleGuidsBetweenAdapters(articleWrapper: ArticleContainersWrapper, articleContainers: ArticleContainer[]): void {
    const articlesWithoutEndPlates: ArticleContainer[] = [];
    for (const articleContainer of articleContainers) {
      if (!ArticleUtils.isEndPlateArticle(articleContainer.article)) {
        articlesWithoutEndPlates.push(articleContainer);
      }
    }
    // in case there are no articles between such connections -> process strip like it is connected to usual articles (not adapters)
    if (articleContainers.length > 0) {
      articleWrapper.properties.setValue(
        ArticleContainerPropertyDef.ARTICLES_GUIDS_BETWEEN_ADAPTERS,
        ArticleContainerUtils.extractArticleGUIDs(articlesWithoutEndPlates)
      );
    }
  }

  private prepareAndSplitConnectionCubes(allConnectionCubes: Connection[], ignoreDistance: boolean = false): Connection[][] {
    let connectionGroups: Connection[][] = [];
    // adapter cubes are processed differently from other cubes
    const adapterCubes: Connection[] = LabelingUtils.extractAdapterConnectionCubes(allConnectionCubes);
    const filteredConnectionCubes: Connection[] = allConnectionCubes.filter(ArrayUtils.notInArray, adapterCubes);
    if (adapterCubes.length > 0) {
      connectionGroups = connectionGroups.concat(
        this.acceptingConnectionSortGroupHelper.splitConnectionCubesOnGroups(adapterCubes, true, ignoreDistance)
      );
    }
    if (filteredConnectionCubes.length > 0) {
      connectionGroups = connectionGroups.concat(
        this.acceptingConnectionSortGroupHelper.splitConnectionCubesOnGroups(filteredConnectionCubes, false, ignoreDistance)
      );
    }
    return connectionGroups;
  }

  public process(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    givingConnectionAreas: ConnectionAreaVO[],
    rotationAngle: number,
    updateTextures: boolean = true
  ): void {
    if (this.projectHelper.isLabelingCorrectionDisabled()) {
      ArticleUsageUtils.setLabelingCorrectionIgnored(baseContainer.articleUsage, true);
    }
    super.process(containers, baseContainer, givingConnectionAreas, rotationAngle, updateTextures);
  }

  protected shouldSwitchPoints(rectangle: Rectangle3D, dynamicArticleContainer: ArticleContainer): boolean {
    const rectangleToCheck: Rectangle3D = Rectangle3D.fromArray(rectangle.toArray());
    this.labelingArticleRotationHelper.transformTextureAreaRectangle(
      rectangleToCheck,
      NaN,
      dynamicArticleContainer.properties.getValue(ArticleContainerPropertyDef.TEXT_ANGLE)
    );
    this._isInversedDirection = ArticleUsageUtils.isLabelingCorrectionIgnored(dynamicArticleContainer.articleUsage)
      ? false
      : this.labelingArticleRotationHelper.isAdditionalRotationRequired(rectangleToCheck);
    return this._isInversedDirection;
  }

  private checkAndSetForDifferentAdaptersWithSameParent(articleWrapper: ArticleContainersWrapper, connectionGroup: Connection[]): boolean {
    // Special case for #64268: Ensure only one Texture is created on a label strip with adapters on 789-665

    // To do this, we set articleGuidsBetweenAdapters to the 789-665 article's articleUsageId.
    // The following conditions must be met for this behavior to apply:

    // * ArticleWrapper (the labeling strip) must be mounted on multiple different parents (adapters)
    const parentArticleUsages: ArticleUsageVO[] = connectionGroup.flatMap(c => c.articleContainer.articleUsage);
    const numberOfDifferentParents = new Set(parentArticleUsages.map(usage => usage.id)).size;
    if (numberOfDifferentParents < 2) {
      return false;
    }

    // * The parents (-> adapters) must be mounted to a single grandparent.
    const grandparentArticleUsages: ArticleUsageVO[] = parentArticleUsages.flatMap(usage => this.articleUsageService.getParentArticleUsages(usage));
    const numberOfDifferentGrandparents = new Set(grandparentArticleUsages.map(usage => usage.id)).size;
    if (numberOfDifferentGrandparents !== 1) {
      return false;
    }
    const theGrandparentArticleUsage = grandparentArticleUsages[0];

    // * The grandparent must not be a mounting rail.
    if (this.articleUsageService.isMountingRailArticleUsage(theGrandparentArticleUsage)) {
      return false;
    }

    // * The grandparent must be mounted to the mounting rail.
    const isMountedOnRail = this.articleUsageService.getParentArticleUsages(theGrandparentArticleUsage)
      .some(usage => this.articleUsageService.isMountingRailArticleUsage(usage));
    if (!isMountedOnRail) {
      return false;
    }

    // -> Set articleGuidsBetweenAdapters to contain the grandparent.
    const articleContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids([theGrandparentArticleUsage.id]);
    this.setArticleGuidsBetweenAdapters(articleWrapper, articleContainers);

    return true;
  }
}
